import React, { useMemo, useCallback } from "react";
import { OverlayView, OverlayViewF } from "@react-google-maps/api";
import { MapServiceRequest } from "../../models/models";
import { dt } from "../../../../shared/themes/redesignTokens";

// ===================================================================
//  DESIGN TOKENS
// ===================================================================


// Priority colors (consistent with CitySidebar chips)
const PRIORITY_COLORS = {
  urgent: "#E74C3C",
  high: "#E67E22",
  low: "#27AE60",
};

// ===================================================================
//  TYPES
// ===================================================================

/** Legacy type kept for backward compatibility */
export interface FlatRequest {
  request: MapServiceRequest;
  lat: number;
  lng: number;
}

/** Company group: one marker per company, with all its requests */
export interface CompanyGroup {
  companyId: string;
  companyName: string;
  logoUrl?: string;
  lat: number;
  lng: number;
  requests: MapServiceRequest[];
  /** City name from the MapLocation this company belongs to */
  city: string;
  countryName: string;
  countryId: number;
}

// ===================================================================
//  MARKER SIZE SCALE (matches CityMarker pattern)
// ===================================================================

const getMarkerSize = (count: number): number => {
  if (count <= 1) return 36;
  if (count <= 3) return 42;
  if (count <= 9) return 48;
  return 56;
};

// ===================================================================
//  PRIORITY HELPERS
// ===================================================================

/**
 * Determine the highest priority color from requests.
 * Priority codes: 1=urgent, 2=high, 4=low (lower number = higher priority).
 */
const getHighestPriorityColorFromRequests = (requests: MapServiceRequest[]): string => {
  let minCode = Infinity;
  for (const r of requests) {
    if (r.priorityTypeCode < minCode) minCode = r.priorityTypeCode;
  }
  if (minCode <= 1) return PRIORITY_COLORS.urgent;
  if (minCode <= 2) return PRIORITY_COLORS.high;
  return PRIORITY_COLORS.low;
};

/**
 * Check if there are any urgent or high priority requests.
 * Priority codes: 1=urgent, 2=high (lower number = higher priority).
 */
const hasUrgentOrHighRequests = (requests: MapServiceRequest[]): boolean => {
  return requests.some((r) => r.priorityTypeCode <= 2);
};

/**
 * Count requests by priority code.
 * Priority codes: 1=urgent, 2=high, 4=low.
 */
const countByPriority = (requests: MapServiceRequest[]) => {
  let urgentCount = 0, highCount = 0, lowCount = 0;
  for (const r of requests) {
    if (r.priorityTypeCode === 1) urgentCount++;
    else if (r.priorityTypeCode === 2) highCount++;
    else lowCount++;
  }
  return { urgentCount, highCount, lowCount };
};

/**
 * Build a conic-gradient string from priority counts.
 * Segments ordered: urgent (red) -> high (orange) -> low (green).
 * Matches the CityMarker conic gradient style.
 */
const buildConicGradient = (
  urgentCount: number,
  highCount: number,
  lowCount: number,
  total: number
): string => {
  if (total === 0) return PRIORITY_COLORS.low;

  const segments: { color: string; count: number }[] = [];
  if (urgentCount > 0) segments.push({ color: PRIORITY_COLORS.urgent, count: urgentCount });
  if (highCount > 0) segments.push({ color: PRIORITY_COLORS.high, count: highCount });
  if (lowCount > 0) segments.push({ color: PRIORITY_COLORS.low, count: lowCount });

  // Single color: return solid
  if (segments.length <= 1) {
    return segments[0]?.color ?? PRIORITY_COLORS.low;
  }

  // Build conic-gradient stops
  let currentDeg = 0;
  const stops: string[] = [];
  for (const seg of segments) {
    const deg = (seg.count / total) * 360;
    stops.push(`${seg.color} ${currentDeg}deg ${currentDeg + deg}deg`);
    currentDeg += deg;
  }

  return `conic-gradient(${stops.join(", ")})`;
};

// ===================================================================
//  COMPONENT
// ===================================================================

interface RfsMarkerProps {
  companyGroup: CompanyGroup;
  isSelected: boolean;
  onClick: (companyGroup: CompanyGroup) => void;
}

const RfsMarker: React.FC<RfsMarkerProps> = ({ companyGroup, isSelected, onClick }) => {
  const { requests, lat, lng, companyName, logoUrl } = companyGroup;
  const totalCount = requests.length;
  const size = getMarkerSize(totalCount);

  // Compute highest priority color (used for pulse ring, badge, selection glow)
  const ringColor = useMemo(
    () => getHighestPriorityColorFromRequests(requests),
    [requests]
  );

  const hasUrgent = useMemo(
    () => hasUrgentOrHighRequests(requests),
    [requests]
  );

  // Build conic gradient background from all priority counts (matches CityMarker style)
  const backgroundStyle = useMemo(() => {
    const { urgentCount, highCount, lowCount } = countByPriority(requests);
    return buildConicGradient(urgentCount, highCount, lowCount, totalCount);
  }, [requests, totalCount]);

  const position = useMemo(() => ({ lat, lng }), [lat, lng]);

  const handleClick = useCallback(() => {
    onClick(companyGroup);
  }, [onClick, companyGroup]);

  const getPixelPositionOffset = useCallback(
    () => ({ x: -(size / 2), y: -(size / 2 + 10) }),
    [size]
  );

  const firstLetter = companyName?.charAt(0)?.toUpperCase() || "?";

  return (
    <OverlayViewF
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
      getPixelPositionOffset={getPixelPositionOffset}
    >
      <div
        onClick={handleClick}
        style={{
          position: "relative",
          width: size,
          height: size,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        {/* Pulsing ring for urgent/high */}
        {hasUrgent && (
          <div
            style={{
              position: "absolute",
              width: size,
              height: size,
              borderRadius: "50%",
              border: `2px solid ${ringColor}`,
              animation: "markerPulse 2.5s ease-in-out infinite",
              top: 0,
              left: 0,
            }}
          />
        )}

        {/* Main circle: conic gradient ring with white center containing logo/avatar */}
        <div
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            background: backgroundStyle,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: isSelected
              ? `0 0 0 3px #FFFFFF, 0 0 0 5px ${ringColor}`
              : "0 2px 8px rgba(0,0,0,0.25)",
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
            transform: isSelected ? "scale(1.1)" : "scale(1)",
          }}
        >
          {/* White interior with logo or first letter */}
          <div
            style={{
              width: size - 6,
              height: size - 6,
              borderRadius: "50%",
              backgroundColor: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                style={{
                  width: size - 8,
                  height: size - 8,
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <span
                style={{
                  fontFamily: dt.font,
                  fontSize: size <= 36 ? "14px" : "16px",
                  fontWeight: 700,
                  color: dt.textSecondary,
                  userSelect: "none",
                }}
              >
                {firstLetter}
              </span>
            )}
          </div>

          {/* Request count badge (always shown, 2x size) */}
          <div
            style={{
              position: "absolute",
              top: -14,
              right: -14,
              minWidth: 36,
              height: 36,
              borderRadius: "18px",
              backgroundColor: ringColor,
              color: "#FFFFFF",
              fontSize: "20px",
              fontWeight: 700,
              fontFamily: dt.font,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 6px",
              border: "2px solid #FFFFFF",
              lineHeight: 1,
              boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
            }}
          >
            {totalCount}
          </div>
        </div>

        {/* Company name label below */}
        <div
          style={{
            position: "absolute",
            bottom: -18,
            left: "50%",
            transform: "translateX(-50%)",
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 700,
            color: dt.textPrimary,
            whiteSpace: "nowrap",
            textShadow: "0 1px 3px rgba(255,255,255,0.9)",
            userSelect: "none",
          }}
        >
          {companyName}
        </div>
      </div>
    </OverlayViewF>
  );
};

export default React.memo(RfsMarker);
